import { GLIFOS, GLIFOS_BOLD, GLIFOS_FILL, type Glifo } from "./glifos.js";

/**
 * O único jeito de um ícone entrar numa tela do Noesis.
 *
 * Ele existe para que duas regras da Etapa 3 sejam estruturais em vez de
 * combinadas — regra combinada some na terceira tela.
 *
 * **1. O traço vem do papel, não do gosto.** O mapa está em `CLASSE_DO_PAPEL`
 * aqui embaixo; um traço escolhido tela a tela desfaz a hierarquia.
 *
 * **2. Ou o ícone é decorativo, ou ele tem nome.** Não existe terceiro caso, e
 * é por isso que `rotulo` decide sozinho:
 *
 *   sem `rotulo`   `aria-hidden`. É o caso comum: o ícone acompanha um texto
 *                  que já diz a mesma coisa, e anunciá-lo seria repetição.
 *   com `rotulo`   `role="img"` mais um `<title>` dentro do SVG.
 *
 * **Em botão só de ícone o rótulo vai no botão, não aqui.** O `aria-label` do
 * `<button>` é o nome acessível do controle; um nome no SVG dentro dele faria
 * o leitor de tela anunciar duas vezes. Então, nesse caso, o ícone é
 * decorativo — sem `rotulo` — e quem carrega o significado é o botão.
 *
 * Tamanho e cor não aparecem em prop nenhuma. O tamanho é a classe `.icone`
 * (invariante 10), e a cor é `currentColor`, herdada do texto ao lado.
 */

/* O papel decide o peso, e não a tela (tema Caderno, ADR 040). O handoff
   pede Phosphor `regular` na lateral, `bold` em botões e carets, e `fill` no
   item ativo da ilha:

     navegacao, estado, ritual, decorativo   regular
     acao                                    bold
     *-cheio, estado-ligado                  fill (cai no bold quando o glifo
                                             não tem versão cheia) */
function desenho(glifo: Glifo, papel: PapelDoIcone): string {
  if (papel === "ritual-cheio" || papel === "estado-ligado") return GLIFOS_FILL[glifo] ?? GLIFOS_BOLD[glifo];
  if (papel === "acao") return GLIFOS_BOLD[glifo];
  return GLIFOS[glifo];
}

export type PapelDoIcone =
  | "ritual"
  | "ritual-cheio"
  | "navegacao"
  | "acao"
  | "estado"
  | "estado-ligado"
  | "decorativo";

const TAMANHO = { p: "icone icone--p", m: "icone", g: "icone icone--g" } as const;

export function Icone({
  glifo,
  papel,
  tamanho = "m",
  rotulo,
  className,
}: {
  glifo: Glifo;
  papel: PapelDoIcone;
  tamanho?: keyof typeof TAMANHO;
  rotulo?: string;
  className?: string;
}) {
  const classes = [TAMANHO[tamanho], className].filter(Boolean).join(" ");
  return (
    <svg
      className={classes}
      viewBox="0 0 256 256"
      {...(rotulo ? { role: "img", "aria-label": rotulo } : { "aria-hidden": true, focusable: false })}
    >
      {rotulo ? <title>{rotulo}</title> : null}
      <path d={desenho(glifo, papel)} />
    </svg>
  );
}
